import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { toast } from 'sonner';
import { Helmet } from 'react-helmet-async';
import { 
  Wallet, TrendingUp, ShoppingCart, Clock, Plus, ArrowRight,
  Star, Zap, Crown, Diamond, AlertTriangle, Check, X,
  CreditCard, Calendar, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const tierConfig = {
  none: { name: 'None', icon: Star, color: 'bg-gray-500', textColor: 'text-gray-500' },
  bronze: { name: 'Bronze', icon: Star, color: 'bg-gradient-to-br from-amber-600 to-amber-800', textColor: 'text-amber-600' },
  silver: { name: 'Silver', icon: Zap, color: 'bg-gradient-to-br from-gray-400 to-gray-600', textColor: 'text-gray-500' },
  gold: { name: 'Gold', icon: Crown, color: 'bg-gradient-to-br from-yellow-500 to-yellow-700', textColor: 'text-yellow-600' },
  platinum: { name: 'Platinum', icon: Diamond, color: 'bg-gradient-to-br from-purple-500 to-purple-700', textColor: 'text-purple-600' },
};

const transactionTypeConfig = {
  topup: { label: 'Credit Topup', icon: ArrowUpRight, color: 'text-green-600', bg: 'bg-green-50' },
  usage: { label: 'Order Payment', icon: ArrowDownRight, color: 'text-red-600', bg: 'bg-red-50' },
  adjustment: { label: 'Admin Adjustment', icon: TrendingUp, color: 'text-blue-600', bg: 'bg-blue-50' },
  refund: { label: 'Refund', icon: ArrowUpRight, color: 'text-green-600', bg: 'bg-green-50' },
  expired: { label: 'Credits Expired', icon: X, color: 'text-gray-500', bg: 'bg-gray-50' },
};

const PartnerDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupAmount, setTopupAmount] = useState(25000);
  const [processingTopup, setProcessingTopup] = useState(false);

  useEffect(() => {
    fetchDashboard();
  }, []);

  const fetchDashboard = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login', { state: { returnTo: '/partner/dashboard' } });
      return;
    }

    try {
      const response = await axios.get(`${API}/partner/dashboard`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // Not a partner - redirect to apply
        navigate('/partner/apply');
      } else {
        toast.error('Failed to load dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTopup = async () => {
    if (topupAmount < 25000) {
      toast.error('Minimum topup is ₹25,000');
      return;
    }

    const token = localStorage.getItem('token');
    setProcessingTopup(true);

    try {
      const response = await axios.post(
        `${API}/partner/topup`,
        { amount: topupAmount },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const { razorpay_order_id, credit_amount, total_payable, gst_amount, razorpay_key, new_tier, new_discount } = response.data;

      const options = {
        key: razorpay_key,
        amount: total_payable * 100,  // Total including GST
        currency: 'INR',
        name: 'BusinessMint PR',
        description: `Partner Credit Topup - ${new_tier.charAt(0).toUpperCase() + new_tier.slice(1)} Tier`,
        order_id: razorpay_order_id,
        handler: async (paymentResponse) => {
          try {
            await axios.post(
              `${API}/partner/topup/verify`,
              {
                razorpay_order_id: paymentResponse.razorpay_order_id,
                razorpay_payment_id: paymentResponse.razorpay_payment_id,
                razorpay_signature: paymentResponse.razorpay_signature
              },
              { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success(`₹${credit_amount.toLocaleString('en-IN')} credits added! Now at ${new_tier} tier (${new_discount}% discount)`);
            setShowTopupModal(false);
            fetchDashboard();
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: dashboardData?.partner?.contact_name,
          email: dashboardData?.partner?.email
        },
        theme: { color: '#D4AF37' }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to initiate topup');
    } finally {
      setProcessingTopup(false);
    }
  };

  // Calculate GST for display
  const calculateTopupGST = (amount) => {
    const gstRate = 18;
    const gst = Math.round(amount * (gstRate / 100));
    return { gst, total: amount + gst };
  };

  const getTierForAmount = (amount) => {
    if (amount >= 100000) return { tier: 'Platinum', discount: 18 };
    if (amount >= 75000) return { tier: 'Gold', discount: 15 };
    if (amount >= 50000) return { tier: 'Silver', discount: 12.5 };
    if (amount >= 25000) return { tier: 'Bronze', discount: 10 };
    return { tier: 'None', discount: 0 };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 py-20 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full"></div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="min-h-screen bg-[#fafafa]">
        <Header />
        <div className="max-w-7xl mx-auto px-6 py-20 text-center">
          <h1 className="font-playfair text-2xl font-bold mb-4">Access Denied</h1>
          <p className="text-[#737373] mb-6">You need to be an approved partner to access this page.</p>
          <button
            onClick={() => navigate('/partner/apply')}
            className="px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] hover:bg-[#B8960F]"
          >
            Apply for Partnership
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const { partner, stats, recent_transactions, tier_config } = dashboardData;
  const currentTier = tierConfig[partner.current_tier] || tierConfig.none;
  const TierIcon = currentTier.icon;

  return (
    <div className="min-h-screen bg-[#fafafa]" data-testid="partner-dashboard">
      <Helmet>
        <title>Partner Dashboard | BusinessMint PR</title>
      </Helmet>
      <Header />

      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-playfair text-3xl font-bold text-[#1a1a1a]">Partner Dashboard</h1>
            <p className="font-manrope text-[#737373]">{partner.company_name}</p>
          </div>
          <button
            onClick={() => setShowTopupModal(true)}
            data-testid="topup-btn"
            className="px-6 py-3 bg-[#D4AF37] text-[#0d0d0d] font-manrope uppercase tracking-widest hover:bg-[#B8960F] transition-colors flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Add Credits
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          {/* Credit Balance */}
          <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-[#fee2e2] rounded-lg">
                <Wallet className="w-6 h-6 text-[#D4AF37]" />
              </div>
              <span className="text-sm text-[#737373] uppercase tracking-widest">Credit Balance</span>
            </div>
            <p className="font-mono text-3xl font-bold text-[#1a1a1a]">
              ₹{partner.credit_balance?.toLocaleString('en-IN') || 0}
            </p>
            {stats.expiring_credits_30_days > 0 && (
              <div className="flex items-center gap-2 mt-2 text-amber-600 text-sm">
                <AlertTriangle className="w-4 h-4" />
                ₹{stats.expiring_credits_30_days.toLocaleString('en-IN')} expiring in 30 days
              </div>
            )}
          </div>

          {/* Current Tier */}
          <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className={`p-3 rounded-lg ${currentTier.color}`}>
                <TierIcon className="w-6 h-6 text-white" />
              </div>
              <span className="text-sm text-[#737373] uppercase tracking-widest">Current Tier</span>
            </div>
            <p className={`text-3xl font-bold ${currentTier.textColor}`}>
              {currentTier.name}
            </p>
            <p className="text-sm text-[#737373] mt-1">
              {partner.discount_rate}% discount on all orders
            </p>
          </div>

          {/* Total Spent */}
          <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-[#dbeafe] rounded-lg">
                <ShoppingCart className="w-6 h-6 text-blue-600" />
              </div>
              <span className="text-sm text-[#737373] uppercase tracking-widest">Total Orders</span>
            </div>
            <p className="font-mono text-3xl font-bold text-[#1a1a1a]">{stats.total_orders}</p>
            <p className="text-sm text-[#737373] mt-1">
              ₹{stats.total_spent?.toLocaleString('en-IN') || 0} total value
            </p>
          </div>

          {/* Total Savings */}
          <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="p-3 bg-[#dcfce7] rounded-lg">
                <TrendingUp className="w-6 h-6 text-green-600" />
              </div>
              <span className="text-sm text-[#737373] uppercase tracking-widest">Lifetime Topup</span>
            </div>
            <p className="font-mono text-3xl font-bold text-[#1a1a1a]">
              ₹{partner.total_topup_amount?.toLocaleString('en-IN') || 0}
            </p>
            <p className="text-sm text-[#737373] mt-1">
              ₹{partner.total_credits_used?.toLocaleString('en-IN') || 0} used
            </p>
          </div>
        </div>

        {/* Tier Info */}
        <div className="bg-white border border-[#e5e5e5] rounded-xl p-6 mb-8">
          <h2 className="font-playfair text-xl font-bold mb-4">Tier Benefits</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(tier_config || {}).map(([tier, config]) => {
              const isCurrentTier = tier === partner.current_tier;
              return (
                <div 
                  key={tier} 
                  className={`p-4 rounded-lg border-2 transition-all ${
                    isCurrentTier ? 'border-[#D4AF37] bg-[#fee2e2]/20' : 'border-[#e5e5e5]'
                  }`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold capitalize">{tier}</span>
                    {isCurrentTier && <Check className="w-5 h-5 text-[#D4AF37]" />}
                  </div>
                  <p className="text-2xl font-mono font-bold text-[#D4AF37]">{config.discount}%</p>
                  <p className="text-xs text-[#737373]">Min: ₹{config.min_topup.toLocaleString('en-IN')}</p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white border border-[#e5e5e5] rounded-xl p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="font-playfair text-xl font-bold">Recent Transactions</h2>
            <button 
              onClick={() => navigate('/partner/transactions')}
              className="text-[#D4AF37] text-sm font-manrope hover:underline flex items-center gap-1"
            >
              View All <ArrowRight className="w-4 h-4" />
            </button>
          </div>

          {recent_transactions && recent_transactions.length > 0 ? (
            <div className="space-y-4">
              {recent_transactions.map((tx) => {
                const typeConfig = transactionTypeConfig[tx.type] || transactionTypeConfig.adjustment;
                const TxIcon = typeConfig.icon;
                const isCredit = tx.amount > 0;

                return (
                  <div key={tx.id} className="flex items-center justify-between p-4 bg-[#fafafa] rounded-lg">
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${typeConfig.bg}`}>
                        <TxIcon className={`w-5 h-5 ${typeConfig.color}`} />
                      </div>
                      <div>
                        <p className="font-semibold text-[#1a1a1a]">{typeConfig.label}</p>
                        <p className="text-sm text-[#737373]">{tx.description || '-'}</p>
                        <p className="text-xs text-[#a3a3a3]">
                          {new Date(tx.created_at).toLocaleDateString('en-IN', {
                            day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className={`font-mono font-bold ${isCredit ? 'text-green-600' : 'text-red-600'}`}>
                        {isCredit ? '+' : ''}₹{Math.abs(tx.amount).toLocaleString('en-IN')}
                      </p>
                      <p className="text-xs text-[#737373]">
                        Balance: ₹{tx.balance_after?.toLocaleString('en-IN')}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-12 text-[#737373]">
              <Wallet className="w-12 h-12 mx-auto mb-4 opacity-30" />
              <p>No transactions yet</p>
              <p className="text-sm">Add credits to get started</p>
            </div>
          )}
        </div>
      </div>

      {/* Topup Modal */}
      {showTopupModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-playfair text-xl font-bold">Add Credits</h3>
              <button onClick={() => setShowTopupModal(false)} className="text-[#737373] hover:text-[#1a1a1a]">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-manrope text-[#737373] mb-2">Credit Amount</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[#737373]">₹</span>
                  <input
                    type="number"
                    value={topupAmount}
                    onChange={(e) => setTopupAmount(Number(e.target.value))}
                    min={25000}
                    step={5000}
                    className="w-full pl-8 pr-4 py-3 border border-[#e5e5e5] focus:border-[#D4AF37] focus:outline-none font-mono text-xl"
                    data-testid="topup-amount-input"
                  />
                </div>
              </div>

              {/* Quick amounts */}
              <div className="flex flex-wrap gap-2">
                {[25000, 50000, 75000, 100000].map((amount) => (
                  <button
                    key={amount}
                    onClick={() => setTopupAmount(amount)}
                    className={`px-4 py-2 border rounded-lg text-sm font-mono transition-colors ${
                      topupAmount === amount
                        ? 'border-[#D4AF37] bg-[#fee2e2] text-[#D4AF37]'
                        : 'border-[#e5e5e5] hover:border-[#D4AF37]'
                    }`}
                  >
                    ₹{amount.toLocaleString('en-IN')}
                  </button>
                ))}
              </div>

              {/* Tier preview */}
              {topupAmount >= 25000 && (
                <div className="bg-[#f5f5f5] rounded-lg p-4">
                  <p className="text-sm text-[#737373] mb-2">With this topup you&apos;ll get:</p>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{getTierForAmount(topupAmount).tier} Tier</span>
                    <span className="font-mono text-[#D4AF37] font-bold">
                      {getTierForAmount(topupAmount).discount}% Discount
                    </span>
                  </div>
                </div>
              )}

              {/* GST Breakdown */}
              {topupAmount >= 25000 && (
                <div className="border border-[#e5e5e5] rounded-lg p-4 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-[#737373]">Credit Amount</span>
                    <span className="font-mono">₹{topupAmount.toLocaleString('en-IN')}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-[#737373]">GST (18%)</span>
                    <span className="font-mono">₹{calculateTopupGST(topupAmount).gst.toLocaleString('en-IN')}</span>
                  </div>
                  <hr className="border-[#e5e5e5]" />
                  <div className="flex justify-between font-semibold">
                    <span>Total Payable</span>
                    <span className="font-mono text-[#D4AF37]">₹{calculateTopupGST(topupAmount).total.toLocaleString('en-IN')}</span>
                  </div>
                </div>
              )}

              <p className="text-xs text-[#737373]">
                <Clock className="w-4 h-4 inline mr-1" />
                Credits expire 6 months from topup date
              </p>
            </div>

            <button
              onClick={handleTopup}
              disabled={processingTopup || topupAmount < 25000}
              data-testid="confirm-topup-btn"
              className="w-full py-4 bg-[#D4AF37] text-[#0d0d0d] font-manrope uppercase tracking-widest hover:bg-[#B8960F] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processingTopup ? (
                <>
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                  Processing...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5" />
                  Pay ₹{calculateTopupGST(topupAmount).total.toLocaleString('en-IN')}
                </>
              )}
            </button>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default PartnerDashboard;
